import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts'
import { apiClient } from '@services/api'

interface Analytics {
  kpis: {
    todaysRevenue: { value: number; changePct: number | null; invoices: number }
    todaysGrossProfit: { value: number; marginPct: number | null }
    monthRevenue: { value: number; changePct: number | null; lastMonth: number; invoices: number }
    monthGrossProfit: { value: number; marginPct: number | null }
    stockValue: { value: number; segments: Array<{ key: string; value: number; pct: number }> }
  }
  receivables: { total: number; overdue: number; overdueInvoices: number; overdueStores: number; avgCollectionDays: number }
  summary: { totalStores: number; activeStores: number }
  routes: Array<{ route: string; sales: number; collected: number; outstanding: number; pct: number }>
  charts: {
    revenue30: Array<{ day: number; date: string; revenue: number; profit: number }>
    cashCredit: Array<{ week: string; cash: number; credit: number }>
    topProducts: Array<{ product: string; revenue: number; revenueInr: number }>
    aging: Array<{ bucket: string; amount: number; amountInr: number; stores: number }>
    stockSegments: Array<{ key: string; segment: string; label: string; value: number; valueInr: number; pct: number }>
    lossTrend: Array<{ day: string; date: string; loss: number }>
  }
}

const C = { primary: '#1F4B44', accent: '#C77D1F', good: '#3C7A4F', danger: '#B3432C', sage: '#8FAE8E', sand: '#E9C889', grid: '#EEEDE7', ink: '#5B6A67' }
const AGING_COLORS = [C.good, C.sage, C.sand, C.danger]
const STOCK_COLORS = [C.sage, C.sand, C.primary]
const formatINR = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const compact = (n: number) => (Math.abs(n) >= 100000 ? `₹${(n / 100000).toFixed(n >= 1000000 ? 1 : 2).replace(/\.?0+$/, '')}L` : formatINR(n))
const lakh = (v: number) => `₹${v}L`
const tooltipStyle = { contentStyle: { borderRadius: 6, border: '1px solid #DDE2DC', fontSize: 12, fontFamily: 'inherit' }, labelStyle: { color: '#5B6A67', fontWeight: 600 } }
const pct = (n: number | null, suffix: string) => (n === null ? '' : `${n >= 0 ? '+' : ''}${n}% ${suffix}`)

export function AnalyticsDashboard() {
  const [range, setRange] = useState<'today' | 'month'>('today')
  const { data, isLoading } = useQuery({ queryKey: ['analytics-dashboard'], queryFn: () => apiClient.get<any>('/analytics/dashboard'), refetchInterval: 60000 })
  const a: Analytics | undefined = data?.data
  const k = a?.kpis
  const isToday = range === 'today'
  const revenue = isToday ? k?.todaysRevenue : k?.monthRevenue
  const profit = isToday ? k?.todaysGrossProfit : k?.monthGrossProfit
  const segTotal = a?.charts.stockSegments.reduce((s, x) => s + x.valueInr, 0) || 0

  return (
    <div>
      <div className="hdr" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1>Analytics Dashboard</h1>
          <p>Overall business performance — today's figures alongside the monthly trend.</p>
        </div>
        <div className="range-toggle">
          <button type="button" className={isToday ? 'on' : ''} onClick={() => setRange('today')}>Today</button>
          <button type="button" className={!isToday ? 'on' : ''} onClick={() => setRange('month')}>This Month</button>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="label">{isToday ? "Today's Revenue" : 'Month Revenue (MTD)'}</div>
          <div className="val">{revenue ? compact(revenue.value) : '—'}</div>
          <div className={`sub ${revenue && revenue.changePct !== null ? (revenue.changePct >= 0 ? 'up' : 'down') : ''}`}>
            {revenue ? (revenue.changePct !== null ? pct(revenue.changePct, isToday ? 'vs yesterday' : 'vs last month') : `${revenue.invoices} invoice${revenue.invoices === 1 ? '' : 's'}`) : ''}
          </div>
        </div>
        <div className="kpi">
          <div className="label">{isToday ? "Today's Gross Profit" : 'Month Gross Profit (MTD)'}</div>
          <div className="val">{profit ? formatINR(profit.value) : '—'}</div>
          <div className={`sub ${profit && (profit.value >= 0 ? 'up' : 'down')}`}>{profit && profit.marginPct !== null ? `${profit.marginPct}% margin — sale price vs traced bulk cost` : 'no sales yet'}</div>
        </div>
        <div className="kpi">
          <div className="label">{isToday ? 'Month Revenue (MTD)' : "Today's Revenue"}</div>
          <div className="val">{k ? compact(isToday ? k.monthRevenue.value : k.todaysRevenue.value) : '—'}</div>
          <div className={`sub ${k && (isToday ? k.monthRevenue.changePct : k.todaysRevenue.changePct) !== null ? ((isToday ? k.monthRevenue.changePct! : k.todaysRevenue.changePct!) >= 0 ? 'up' : 'down') : ''}`}>
            {k ? (isToday ? (k.monthRevenue.changePct !== null ? pct(k.monthRevenue.changePct, 'vs last month') : `${k.monthRevenue.invoices} invoices this month`) : (k.todaysRevenue.changePct !== null ? pct(k.todaysRevenue.changePct, 'vs yesterday') : `${k.todaysRevenue.invoices} invoices today`)) : ''}
          </div>
        </div>
        <div className="kpi">
          <div className="label">Total Stock Value (all segments)</div>
          <div className="val">{k ? compact(k.stockValue.value) : '—'}</div>
          <div className="sub">Raw + In Repacking + Finished</div>
        </div>
      </div>

      <div className="an-grid2 !mt-0">
        <div className="chartcard">
          <h2>Revenue &amp; Gross Profit — Last 30 Days</h2>
          <div className="cap">Daily sales revenue against gross profit (sale price vs traced bulk cost), ₹ lakhs</div>
          <div className="chart-wrap h260">
            {a && (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={a.charts.revenue30} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="anRev" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.primary} stopOpacity={0.25} /><stop offset="100%" stopColor={C.primary} stopOpacity={0.02} /></linearGradient>
                  </defs>
                  <CartesianGrid stroke={C.grid} vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} interval={4} />
                  <YAxis tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} tickFormatter={lakh} width={48} />
                  <Tooltip {...tooltipStyle} formatter={(v: number, n: string) => [`₹${v}L`, n === 'revenue' ? 'Revenue' : 'Gross profit']} labelFormatter={(d) => `Day ${d}`} />
                  <Area type="monotone" dataKey="revenue" stroke={C.primary} strokeWidth={2} fill="url(#anRev)" />
                  <Line type="monotone" dataKey="profit" stroke={C.accent} strokeWidth={2} dot={false} />
                  <Area type="monotone" dataKey="profit" stroke={C.accent} strokeWidth={2} fill="transparent" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="legend-row"><span><i style={{ background: C.primary }} />Revenue</span><span><i style={{ background: C.accent }} />Gross profit</span></div>
        </div>
        <div className="chartcard">
          <h2>Stock Value by Segment</h2>
          <div className="cap">Today, ₹ lakhs — raw material, in repacking, finished goods</div>
          <div className="chart-wrap h260">
            {a && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={a.charts.stockSegments} dataKey="value" nameKey="segment" innerRadius={62} outerRadius={95} paddingAngle={2} stroke="#fff">
                    {a.charts.stockSegments.map((s, i) => <Cell key={s.key} fill={STOCK_COLORS[i % STOCK_COLORS.length]} />)}
                  </Pie>
                  <Tooltip {...tooltipStyle} formatter={(v: number, n: string) => [`₹${v}L`, n]} />
                  <text x="50%" y="47%" textAnchor="middle" fontSize={18} fontWeight={700} fill={C.primary}>{compact(k?.stockValue.value || 0)}</text>
                  <text x="50%" y="56%" textAnchor="middle" fontSize={11} fill={C.ink}>total stock</text>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="legend-row"><span><i style={{ background: C.sage }} />Raw material</span><span><i style={{ background: C.sand }} />In repacking</span><span><i style={{ background: C.primary }} />Finished goods</span></div>
        </div>
      </div>

      <div className="an-grid3">
        <div className="chartcard">
          <h2>Cash vs Credit Sales</h2>
          <div className="cap">This month, ₹ lakhs per week</div>
          <div className="chart-wrap h220">
            {a && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={a.charts.cashCredit} margin={{ top: 8, right: 8, left: 0, bottom: 0 }} barGap={4}>
                  <CartesianGrid stroke={C.grid} vertical={false} />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} tickFormatter={lakh} width={44} />
                  <Tooltip {...tooltipStyle} formatter={(v: number, n: string) => [`₹${v}L`, n === 'cash' ? 'Cash / UPI' : 'Credit']} />
                  <Bar dataKey="cash" fill={C.good} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="credit" fill={C.accent} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="legend-row"><span><i style={{ background: C.good }} />Cash / UPI</span><span><i style={{ background: C.accent }} />Credit</span></div>
        </div>
        <div className="chartcard">
          <h2>Top Products by Revenue</h2>
          <div className="cap">Last 30 days, ₹ lakhs</div>
          <div className="chart-wrap h220">
            {a && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={a.charts.topProducts} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid stroke={C.grid} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} tickFormatter={lakh} />
                  <YAxis type="category" dataKey="product" tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} width={90} />
                  <Tooltip {...tooltipStyle} formatter={(v: number) => [`₹${v}L`, 'Revenue']} />
                  <Bar dataKey="revenue" fill={C.primary} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="chartcard">
          <h2>Credit Aging</h2>
          <div className="cap">Outstanding {a ? compact(a.receivables.total) : '—'} across {a?.summary.activeStores ?? '—'} stores</div>
          <div className="chart-wrap h220">
            {a && (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={a.charts.aging} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={C.grid} vertical={false} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10.5, fill: C.ink }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} tickFormatter={lakh} width={44} />
                  <Tooltip {...tooltipStyle} formatter={(v: number, _n: string, p: any) => [`₹${v}L · ${p.payload.stores} store${p.payload.stores === 1 ? '' : 's'}`, 'Outstanding']} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {a.charts.aging.map((b, i) => <Cell key={b.bucket} fill={AGING_COLORS[i % AGING_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="legend-row"><Link to="/dashboard/collections" className="text-primary-500 hover:underline">Open Sales &amp; Collections →</Link></div>
        </div>
      </div>

      <div className="an-grid2">
        <div className="chartcard">
          <h2>Repacking Loss Trend</h2>
          <div className="cap">Validated loss value, ₹ per day (last 7 days)</div>
          <div className="chart-wrap h220">
            {a && (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={a.charts.lossTrend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={C.grid} vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: C.ink }} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v}`} width={52} />
                  <Tooltip {...tooltipStyle} formatter={(v: number) => [formatINR(v), 'Loss value']} />
                  <Line type="monotone" dataKey="loss" stroke={C.danger} strokeWidth={2} dot={{ r: 3, fill: C.danger }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="legend-row"><Link to="/dashboard/repacking" className="text-primary-500 hover:underline">Open Repacking Entry →</Link></div>
        </div>
        <div className="chartcard">
          <h2>Stock Value by Segment — Table</h2>
          <div className="cap">Today, exact figures</div>
          <table>
            <thead><tr><th>Segment</th><th className="num">Value</th><th className="num">% of Total</th></tr></thead>
            <tbody>
              {(a?.charts.stockSegments || []).map((s) => (
                <tr key={s.key}><td>{s.label}</td><td className="num">{formatINR(s.valueInr)}</td><td className="num">{segTotal > 0 ? Math.round((s.valueInr / segTotal) * 100) : 0}%</td></tr>
              ))}
              <tr><td><b>Total</b></td><td className="num"><b>{formatINR(segTotal)}</b></td><td className="num"><b>100%</b></td></tr>
            </tbody>
          </table>
          <div className="legend-row"><Link to="/dashboard/stock" className="text-primary-500 hover:underline">Open Stock for Sale →</Link></div>
        </div>
      </div>

      <div className="chartcard" style={{ marginTop: 18 }}>
        <h2>Branch / Route Performance</h2>
        <div className="cap">This month — sales, collections, and outstanding credit</div>
        <table>
          <thead><tr><th>Route</th><th className="num">Sales</th><th className="num">Collected</th><th className="num">Outstanding</th><th>Collection %</th></tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="text-ink-soft">Loading…</td></tr>}
            {!isLoading && (a?.routes || []).length === 0 && <tr><td colSpan={5} className="text-ink-soft">No sales this month yet.</td></tr>}
            {(a?.routes || []).map((r) => (
              <tr key={r.route}>
                <td>{r.route}</td>
                <td className="num">{compact(r.sales)}</td>
                <td className="num">{compact(r.collected)}</td>
                <td className="num">{compact(r.outstanding)}</td>
                <td><span className={`badge ${r.pct >= 85 ? 'badge-ok' : 'badge-loss'}`}>{r.pct}%</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
